import { FormikHelpers } from "formik";
import React, { useCallback, useEffect, useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { useCustomerServiceApi } from "@api/hooks/customerService.hook";

import { trackContactStepSubmitted } from "@services/tracking";

import { useThunkDispatch } from "@redux/dispatcher";
import * as CustomerSelectors from "@redux/modules/api/data/customer/customer.selectors";
import * as PhonenumberPrefixesSelectors from "@redux/modules/api/data/phonenumberPrefixes/phonenumberPrefixes.selectors";
import * as StoresModule from "@redux/modules/api/data/stores/stores.module";
import * as StoresSelectors from "@redux/modules/api/data/stores/stores.selectors";
import {
    broadcastMessage,
    clearMessageById,
} from "@redux/modules/value/messages/messages.utils";
import {
    Translate,
    useGranite,
} from "@redux/modules/value/translations/translations.provider";

import {
    categoriesToFormOptions,
    emptyFormValues,
    getCategoryByCode,
    getInitialFormValues,
    getPhone,
} from "@components/ContactCustomerservice/helpers/formValues.helpers";

import ContactCustomerservice from "./contactCustomerservice.component";
import {
    Category,
    ContactCustomerServiceForm,
    ContactCustomerServiceRequest,
} from "./contactCustomerservice.types";

const broadcastSuccessMessage = (t: Translate) => () =>
    broadcastMessage({
        id: "contact-customerservice-request-success",
        text: t("contact.customerservice.request.success.message"),
        status: "success",
        dismissible: true,
        activeScroll: true,
    });

const broadcastFailureMessage = (t: Translate) => () =>
    broadcastMessage({
        id: "contact-customerservice-request-failure",
        text: t("contact.customerservice.request.failure.message"),
        status: "danger",
        dismissible: true,
        activeScroll: true,
    });

type ContactCustomerserviceContainerProps = {
    categories: Array<Category>;
};

const ContactCustomerserviceContainer = ({
    categories,
}: ContactCustomerserviceContainerProps) => {
    const isLoadingCustomer = useSelector(CustomerSelectors.isLoading);
    const isLoadingPhonenumberPrefixes = useSelector(
        PhonenumberPrefixesSelectors.isLoading,
    );
    const customer = useSelector(CustomerSelectors.getCustomer, shallowEqual);
    const phonenumberPrefixes = useSelector(
        PhonenumberPrefixesSelectors.getPhonenumberPrefixes,
        shallowEqual,
    );

    const { t } = useGranite();

    const [{ loading }, contactWeb2Case] =
        useCustomerServiceApi<ContactCustomerServiceRequest>(
            {
                url: "/contactform",
                method: "POST",
            },
            { manual: true, useCache: false },
        );

    const categoriesFormOptions = useMemo(
        () => categoriesToFormOptions(categories),
        [categories],
    );

    const initialFormValues = useMemo(() => {
        return getInitialFormValues(customer, categories);
    }, [customer, categories]);

    const handleSubmit = useCallback(
        async (
            {
                email,
                storeId,
                message,
                phone,
                phonePrefix,
                firstName,
                lastName,
                category,
                bonusCardNumber,
                orderId,
            }: ContactCustomerServiceForm,
            { resetForm }: FormikHelpers<ContactCustomerServiceForm>,
        ): Promise<void> => {
            clearMessageById("contact-customerservice-request-success");

            const request: ContactCustomerServiceRequest = {
                firstName: firstName,
                lastName: lastName,
                message: message,
                storeId: storeId,
                orderId: orderId,
                category: category,
                bonusCardNumber: bonusCardNumber,
                email: email,
                phone: getPhone(phonenumberPrefixes, phonePrefix, phone),
            };

            return contactWeb2Case({ data: request })
                .then(broadcastSuccessMessage(t))
                .then(() => {
                    trackContactStepSubmitted({
                        storeId: request.storeId,
                        email: request.email,
                        rubriek: getCategoryByCode(categories)(
                            request.category,
                        ),
                    });
                    resetForm({ values: emptyFormValues });
                })
                .catch(broadcastFailureMessage(t));
        },
        [phonenumberPrefixes, categories, t],
    );

    return (
        <ContactCustomerservice
            onSubmit={handleSubmit}
            isSubmitting={loading}
            initialFormValues={initialFormValues}
            categoriesFormOptions={categoriesFormOptions}
            loading={isLoadingCustomer || isLoadingPhonenumberPrefixes}
        />
    );
};

export { ContactCustomerserviceContainer };
